import { CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Switch,
  Tooltip,
} from 'antd'
import { useWatch } from 'antd/es/form/Form'
import dayjs from 'dayjs'
import { PromoCodeFormMode, PromoCodeFormValues, PromoCodeItem } from 'Modules'
import { useEffect, useRef, useState } from 'react'

import SelectPto from 'component/select/select-pto'
import {
  generatePromoCode,
  getPromoCodeValueAffix,
  isPromoCodeFieldEditable,
  PROMO_CODE_FIELD_MAX_LENGTH,
  PROMO_CODE_MESSAGES,
  PROMO_CODE_PTO_SOLD_OUT_TOOLTIP,
  PROMO_CODE_TOKEN_TYPE_OPTIONS,
  PROMO_CODE_TOTAL_SUPPLY_TYPE_OPTIONS,
  PROMO_CODE_TYPE_OPTIONS,
  PROMO_CODE_USAGE_LIMIT_OPTIONS,
} from 'constants/promo-code'
import { useCreatePromoCode, usePromoCodeDetail, useUpdatePromoCode } from 'hooks/promo-code'

/** PTO selling-status filter codes (mirrors the booster form). */
const PTO_SOLD_OUT_STATUS = { ON_SALE: 0, SOLD_OUT: 1 } as const

/** Pull a human-readable message out of an axios/server error. */
const getServerMessage = (err: unknown, fallback: string): string => {
  const data = (err as { response?: { data?: { message?: string } } })?.response?.data
  return data?.message || (err as Error)?.message || fallback
}

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm:ss'
const MAX = PROMO_CODE_FIELD_MAX_LENGTH
const MSG = PROMO_CODE_MESSAGES

const CREATE_DEFAULTS: Partial<PromoCodeFormValues> = {
  type: 'DISCOUNT',
  totalSupplyType: 'UNLIMITED',
  usageLimit: 'UNLIMITED',
  ptoSoldOut: false,
  tokenType: 'Mey',
  linkedPtoIds: [],
}

const recordToFormValues = (record: PromoCodeItem): PromoCodeFormValues => ({
  code: record.code,
  type: record.type,
  value: record.value,
  bonusBuy: record.bonusBuy,
  bonusGet: record.bonusGet,
  tokenType: record.tokenType ?? 'Mey',
  ptoSoldOut: record.ptoSoldOut ?? false,
  totalSupplyType: record.totalSupply == null ? 'UNLIMITED' : 'LIMITED',
  totalSupply: record.totalSupply,
  usageLimit: record.usageLimit ?? 'UNLIMITED',
  startDate: record.startDate ? dayjs(record.startDate) : undefined,
  endDate: record.endDate ? dayjs(record.endDate) : undefined,
  linkedPtoIds: record.linkedPtos?.map((pto) => pto.id) ?? [],
})

const isAntdValidateError = (err: unknown): boolean =>
  !!err && typeof err === 'object' && 'errorFields' in (err as Record<string, unknown>)

interface PromoCodeFormModalProps {
  open: boolean
  /** Initial mode; "view" can switch to "edit" internally via the Edit button. */
  mode: PromoCodeFormMode
  /** The selected row for view/edit; null for create. */
  record: PromoCodeItem | null
  /** Whether the current admin can create/edit (gates the Edit + submit buttons). */
  canEdit: boolean
  onClose: () => void
}

export const PromoCodeFormModal = ({
  open,
  mode: initialMode,
  record,
  canEdit,
  onClose,
}: PromoCodeFormModalProps) => {
  const [form] = Form.useForm<PromoCodeFormValues>()
  const [mode, setMode] = useState<PromoCodeFormMode>(initialMode)

  const createPromoCode = useCreatePromoCode()
  const updatePromoCode = useUpdatePromoCode()
  const saving = createPromoCode.isPending || updatePromoCode.isPending

  const type = useWatch('type', form)
  const totalSupplyType = useWatch('totalSupplyType', form)
  const isBonus = type === 'PTO_BONUS'
  const showTokenType = type === 'CASHBACK_TOKEN'
  const showSoldOut = type === 'CASHBACK_TOKEN' || type === 'PTO_BONUS'

  // View/Edit prefill from the full detail (GET :id); the list row is the
  // immediate fallback while the detail loads.
  const { detail } = usePromoCodeDetail(open && initialMode !== 'create' ? record?.id : undefined)
  const source = detail ?? record

  const status = source?.status
  const editable = (field: Parameters<typeof isPromoCodeFieldEditable>[0]) =>
    isPromoCodeFieldEditable(field, mode, status)

  // Reset the working mode each time the modal (re)opens.
  useEffect(() => {
    if (open) setMode(initialMode)
  }, [open, initialMode])

  // Prefill once from the list row, then once more when the detail arrives —
  // tracked via a ref so a later detail refetch never clobbers in-progress edits.
  const prefillKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!open) {
      prefillKeyRef.current = null
      return
    }
    if (initialMode === 'create' || !record) {
      if (prefillKeyRef.current !== 'create') {
        form.resetFields()
        form.setFieldsValue(CREATE_DEFAULTS)
        prefillKeyRef.current = 'create'
      }
      return
    }
    const detailKey = `detail:${record.id}`
    if (prefillKeyRef.current === detailKey) return
    const key = detail ? detailKey : `row:${record.id}`
    if (prefillKeyRef.current !== key) {
      form.setFieldsValue(recordToFormValues(detail ?? record))
      prefillKeyRef.current = key
    }
  }, [open, initialMode, record, detail, form])

  const handleGenerate = () => {
    form.setFieldValue('code', generatePromoCode())
    form.validateFields(['code']).catch(() => undefined)
  }

  const handleCopyCode = () => {
    const code = form.getFieldValue('code') as string | undefined
    if (!code || !navigator.clipboard) return
    navigator.clipboard.writeText(code).then(
      () => message.success('Copied to clipboard'),
      () => message.error('Failed to copy')
    )
  }

  const handleCreate = async () => {
    try {
      const values = await form.validateFields()
      await createPromoCode.mutateAsync(values)
      message.success(MSG.CREATE_SUCCESS)
      onClose()
    } catch (err) {
      if (isAntdValidateError(err)) return
      const msg = getServerMessage(err, 'Failed to create promo code')
      // Surface a duplicate-code error on the field; everything else as a toast.
      if (msg === MSG.CODE_EXISTS) {
        form.setFields([{ name: 'code', errors: [msg] }])
      } else {
        message.error(msg)
      }
    }
  }

  const handleSaveEdit = async () => {
    if (!record) return
    try {
      const values = await form.validateFields()
      await updatePromoCode.mutateAsync({ id: record.id, values })
      message.success(MSG.EDIT_SUCCESS)
      setMode('view')
    } catch (err) {
      if (isAntdValidateError(err)) return
      message.error(getServerMessage(err, 'Failed to save changes'))
    }
  }

  const handleCancelEdit = () => {
    setMode('view')
    if (source) form.setFieldsValue(recordToFormValues(source))
  }

  const renderFooter = () => {
    if (mode === 'view') {
      return canEdit ? (
        <Button type="primary" onClick={() => setMode('edit')}>
          Edit
        </Button>
      ) : null
    }
    if (mode === 'edit') {
      return (
        <>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSaveEdit}>
            Save
          </Button>
        </>
      )
    }
    return (
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="primary" loading={saving} onClick={handleCreate}>
          Create Promo Code
        </Button>
      </>
    )
  }

  const affix = getPromoCodeValueAffix(type ?? 'DISCOUNT')
  const showGenerate = mode !== 'view' && editable('code')

  return (
    <Modal
      open={open}
      title={mode === 'create' ? 'Create Promo Code' : 'Promo Code Details'}
      onCancel={onClose}
      width={600}
      centered
      className="promo-code-form"
      footer={renderFooter}
    >
      <Form form={form} layout="vertical" requiredMark className="promo-code-form__body">
        <Form.Item label="Promo Code Content" required style={{ marginBottom: 16 }}>
          <Flex gap={8} align="start">
            <Form.Item
              name="code"
              noStyle
              rules={[
                { required: true, message: MSG.REQUIRED('Promo Code Content') },
                { pattern: /^\S+$/, message: 'Code content cannot contain spaces' },
              ]}
            >
              <Input
                placeholder="Enter code content"
                maxLength={MAX.code}
                disabled={!editable('code')}
                suffix={
                  mode === 'view' ? (
                    <Tooltip title="Copy code">
                      <CopyOutlined className="promo-code-form__copy" onClick={handleCopyCode} />
                    </Tooltip>
                  ) : (
                    <span />
                  )
                }
              />
            </Form.Item>
            {showGenerate ? <Button onClick={handleGenerate}>Generate Code</Button> : null}
          </Flex>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label="Type"
              rules={[{ required: true, message: MSG.REQUIRED('Type') }]}
            >
              <Select options={PROMO_CODE_TYPE_OPTIONS} disabled={!editable('type')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            {isBonus ? (
              <Form.Item label="Value" required style={{ marginBottom: 16 }}>
                <Flex gap={8} align="center" wrap="wrap">
                  <span>Buy</span>
                  <Form.Item
                    name="bonusBuy"
                    noStyle
                    rules={[
                      { required: true, message: MSG.REQUIRED('Value') },
                      { type: 'number', min: 1, message: MSG.MIN_ONE('Value') },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={99}
                      precision={0}
                      controls={false}
                      disabled={!editable('value')}
                      style={{ width: 64 }}
                    />
                  </Form.Item>
                  <span>get</span>
                  <Form.Item
                    name="bonusGet"
                    noStyle
                    rules={[
                      { required: true, message: MSG.REQUIRED('Value') },
                      { type: 'number', min: 1, message: MSG.MIN_ONE('Value') },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={99}
                      precision={0}
                      controls={false}
                      disabled={!editable('value')}
                      style={{ width: 64 }}
                    />
                  </Form.Item>
                  <span>PTO</span>
                </Flex>
              </Form.Item>
            ) : (
              <Form.Item
                name="value"
                label="Value"
                rules={[
                  { required: true, message: MSG.REQUIRED('Value') },
                  { type: 'number', min: 1, message: MSG.MIN_ONE('Value') },
                ]}
              >
                <InputNumber
                  min={1}
                  max={100}
                  precision={0}
                  controls={false}
                  prefix={affix.prefix}
                  suffix={affix.suffix}
                  disabled={!editable('value')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}
          </Col>
        </Row>

        {showTokenType || showSoldOut ? (
          <Row gutter={16}>
            {showTokenType ? (
              <Col span={12}>
                <Form.Item
                  name="tokenType"
                  label="Token Type"
                  rules={[{ required: true, message: MSG.REQUIRED('Token Type') }]}
                >
                  <Select
                    options={PROMO_CODE_TOKEN_TYPE_OPTIONS}
                    disabled={!editable('tokenType')}
                  />
                </Form.Item>
              </Col>
            ) : null}
            {showSoldOut ? (
              <Col span={12}>
                <Form.Item
                  name="ptoSoldOut"
                  valuePropName="checked"
                  label={
                    <Flex gap={6} align="center">
                      <span>PTO Sold Out</span>
                      <Tooltip title={PROMO_CODE_PTO_SOLD_OUT_TOOLTIP}>
                        <QuestionCircleOutlined className="promo-code-form__hint-icon" />
                      </Tooltip>
                    </Flex>
                  }
                >
                  <Switch disabled={!editable('ptoSoldOut')} />
                </Form.Item>
              </Col>
            ) : null}
          </Row>
        ) : null}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="totalSupplyType"
              label="Total Supply"
              required
              style={{ marginBottom: 8 }}
            >
              <Radio.Group
                options={PROMO_CODE_TOTAL_SUPPLY_TYPE_OPTIONS}
                disabled={!editable('totalSupply')}
              />
            </Form.Item>
            {totalSupplyType === 'LIMITED' ? (
              <Form.Item
                name="totalSupply"
                rules={[
                  { required: true, message: MSG.REQUIRED('Total supply') },
                  { type: 'number', min: 1, message: MSG.MIN_ONE('Total supply') },
                ]}
              >
                <InputNumber
                  placeholder="Enter number of code"
                  min={1}
                  max={9999999999}
                  precision={0}
                  controls={false}
                  disabled={!editable('totalSupply')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            ) : null}
          </Col>
          <Col span={12}>
            <Form.Item name="usageLimit" label="Usage Limit" required>
              <Radio.Group
                options={PROMO_CODE_USAGE_LIMIT_OPTIONS}
                disabled={!editable('usageLimit')}
              />
            </Form.Item>
          </Col>
        </Row>

        <div className="promo-code-form__section-label">Duration</div>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="startDate"
              label="Start date"
              rules={[{ required: true, message: MSG.REQUIRED('Start date') }]}
            >
              <DatePicker
                showTime
                format={DATE_TIME_FORMAT}
                placeholder="Select start date"
                style={{ width: '100%' }}
                disabled={!editable('duration')}
                disabledDate={(current) => !!current && current < dayjs().startOf('day')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="endDate"
              label="End date (optional)"
              dependencies={['startDate']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const start = getFieldValue('startDate')
                    if (!value || !start) return Promise.resolve()
                    if (dayjs(value).isAfter(dayjs(start))) return Promise.resolve()
                    return Promise.reject(new Error(MSG.END_BEFORE_START))
                  },
                }),
              ]}
            >
              <DatePicker
                showTime
                format={DATE_TIME_FORMAT}
                placeholder="Select end date"
                style={{ width: '100%' }}
                disabled={!editable('duration')}
                disabledDate={(current) => {
                  const start = form.getFieldValue('startDate')
                  const floor = start ? dayjs(start).startOf('day') : dayjs().startOf('day')
                  return !!current && current < floor
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="linkedPtoIds" label="Linked PTO Projects (optional)">
          <SelectPto
            mode="multiple"
            soldOut={PTO_SOLD_OUT_STATUS.ON_SALE}
            placeholder="Search PTO"
            pageSize={100}
            maxTagCount="responsive"
            disabled={!editable('linkedPtos')}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
